// Loom-style clip recording: capture the screen, composite the camera as a circular
// picture-in-picture bubble, mix screen audio + mic, and record the canvas to a webm.
// Camera is optional — if it's denied the clip is screen-only. Returns a session whose
// stop() resolves the final blob + metadata.

export type ClipResult = { blob: Blob; durationMs: number; width: number; height: number };

export type ClipSession = {
  stop: () => Promise<ClipResult>;
  onScreenEnded: (fn: () => void) => void;
};

function pickMime(): string {
  const prefs = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm'];
  for (const m of prefs) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)) return m;
  }
  return 'video/webm';
}

export async function startScreenCameraClip(
  camDeviceId?: string,
  micDeviceId?: string,
): Promise<ClipSession> {
  const screen = await navigator.mediaDevices.getDisplayMedia({
    video: { frameRate: 30 },
    audio: true,
  });

  let camera: MediaStream | null = null;
  try {
    camera = await navigator.mediaDevices.getUserMedia({
      video: camDeviceId ? { deviceId: camDeviceId } : true,
      audio: micDeviceId ? { deviceId: micDeviceId } : true,
    });
  } catch {
    camera = null; // camera/mic optional — screen-only still records
  }

  const screenVideo = document.createElement('video');
  screenVideo.srcObject = screen;
  screenVideo.muted = true;
  await screenVideo.play().catch(() => {});

  const camVideo = document.createElement('video');
  if (camera) {
    camVideo.srcObject = camera;
    camVideo.muted = true;
    await camVideo.play().catch(() => {});
  }

  const W = screenVideo.videoWidth || 1280;
  const H = screenVideo.videoHeight || 720;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;

  let raf = 0;
  const draw = () => {
    try {
      ctx.drawImage(screenVideo, 0, 0, W, H);
      if (camera && camVideo.videoWidth) {
        const d = Math.round(Math.min(W, H) * 0.22); // bubble diameter
        const x = W - d - 20;
        const y = H - d - 20;
        const cw = camVideo.videoWidth;
        const chh = camVideo.videoHeight;
        const s = Math.min(cw, chh); // square crop, centered
        ctx.save();
        ctx.beginPath();
        ctx.arc(x + d / 2, y + d / 2, d / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(camVideo, (cw - s) / 2, (chh - s) / 2, s, s, x, y, d, d);
        ctx.restore();
        ctx.beginPath();
        ctx.arc(x + d / 2, y + d / 2, d / 2, 0, Math.PI * 2);
        ctx.lineWidth = 3;
        ctx.strokeStyle = 'rgba(255,255,255,0.85)';
        ctx.stroke();
      }
    } catch {
      /* a frame may not be ready yet — skip it */
    }
    raf = requestAnimationFrame(draw);
  };
  draw();

  // Mix any screen audio with the mic into a single track.
  const ac = new AudioContext();
  const dest = ac.createMediaStreamDestination();
  const addAudio = (s: MediaStream | null) => {
    if (s && s.getAudioTracks().length) {
      try {
        ac.createMediaStreamSource(s).connect(dest);
      } catch {
        /* ignore an unconnectable source */
      }
    }
  };
  addAudio(screen);
  addAudio(camera);

  const canvasStream = canvas.captureStream(30);
  const mixed = new MediaStream([
    ...canvasStream.getVideoTracks(),
    ...dest.stream.getAudioTracks(),
  ]);

  const mime = pickMime();
  // 1.5 Mbps keeps a ~2-min clip near ~22 MB — under the upload cap and Whisper's
  // 25 MB transcription limit. VP9 handles screen/text content well at this rate.
  const rec = new MediaRecorder(mixed, { mimeType: mime, videoBitsPerSecond: 1_500_000 });
  const chunks: Blob[] = [];
  rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
  const startedAt = Date.now();
  rec.start();

  let endedCb: (() => void) | null = null;
  screen.getVideoTracks()[0]?.addEventListener('ended', () => endedCb?.());

  const cleanup = () => {
    cancelAnimationFrame(raf);
    screen.getTracks().forEach((t) => t.stop());
    camera?.getTracks().forEach((t) => t.stop());
    ac.close().catch(() => {});
  };

  const result = (): ClipResult => ({
    blob: new Blob(chunks, { type: mime }),
    durationMs: Date.now() - startedAt,
    width: W,
    height: H,
  });

  return {
    onScreenEnded: (fn) => {
      endedCb = fn;
    },
    stop: () =>
      new Promise<ClipResult>((resolve) => {
        if (rec.state === 'inactive') {
          cleanup();
          resolve(result());
          return;
        }
        rec.onstop = () => {
          cleanup();
          resolve(result());
        };
        rec.stop();
      }),
  };
}
